import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Building2,
  CircleDollarSign,
  Layers3,
  RefreshCw,
  Save,
  Search,
  Settings2,
  ShieldCheck
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";

type PlatformSettings = Record<string, any>;

interface SchoolRecord {
  id: number;
  name: string;
  subdomain?: string | null;
  status?: string | null;
  subscription_status?: string | null;
  subscription_tier?: string | null;
  is_subscription_exempt?: number | boolean | null;
}

interface FeatureFlagsRecord {
  tenant_id: number;
  flags: Record<string, boolean>;
  explicit_flags?: Record<string, boolean>;
  enabled_modules?: string[];
  plan_package?: string;
  plan_module_defaults?: Record<string, boolean>;
}

interface FinancialOverviewResponse {
  totals?: {
    feesCollected?: number;
    payrollExpenses?: number;
    subscriptionRevenue?: number;
  };
  subscriptions?: {
    active?: number;
    expired?: number;
    past_due?: number;
    cancelled?: number;
  };
}

const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  students: "Students",
  teachers: "Teachers",
  classes: "Classes",
  results: "Results",
  report_cards: "Report Cards",
  attendance: "Attendance",
  fees: "Fees & Payments",
  payroll: "Payroll",
  messaging: "Messaging",
  uniform_orders: "Uniform Orders",
  library: "Library",
  expenses: "School Expenses",
  transport: "Transport",
  timetable: "Timetable",
  notices: "Notices",
  financial_reports: "Financial Reports"
};

const MODULE_KEYS = Object.keys(MODULE_LABELS);

const prettyJson = (value: any) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
};

const parseJson = (value: string, fallback: any = {}) => {
  try {
    return JSON.parse(value || "{}");
  } catch {
    return fallback;
  }
};

const asString = (value: any, fallback = "") => (value === undefined || value === null ? fallback : String(value));
const asNumberString = (value: any, fallback = "0") => {
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : fallback;
};
const asBoolean = (value: any, fallback = false) => (typeof value === "boolean" ? value : fallback);

const SystemSettings: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [settings, setSettings] = useState<PlatformSettings>({});
  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [featureFlags, setFeatureFlags] = useState<Record<string, FeatureFlagsRecord>>({});
  const [selectedSchool, setSelectedSchool] = useState<number | "">("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [featureFlagsJson, setFeatureFlagsJson] = useState("{}");
  const [financialOverview, setFinancialOverview] = useState<FinancialOverviewResponse | null>(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState<{ enabled?: boolean; message?: string } | null>(null);

  const [templateDraft, setTemplateDraft] = useState({
    primaryColor: "#0f172a",
    secondaryColor: "#0ea5e9",
    schoolMotto: "",
    academicYearStart: "",
    academicYearEnd: "",
    activeTerm: "Term 1",
    numberOfTerms: "3",
    schoolSections: ["Primary"] as string[],
    maxStudents: "300",
    maxTeachers: "30",
    maxAdmins: "3"
  });
  const [templateJson, setTemplateJson] = useState("{}");

  const [subscriptionDraft, setSubscriptionDraft] = useState({
    defaultPlan: "basic",
    renewalCycle: "annual",
    trialDays: "14",
    gracePeriodDays: "7",
    requireBillingApproval: false,
    blockedAccessMessage: "This school subscription has expired. Please contact your administrator or billing support to renew access."
  });
  const [subscriptionJson, setSubscriptionJson] = useState("{}");

  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useAutoDismiss(message, setMessage, "", 5000);
  useAutoDismiss(error, setError, "", 7000);

  const hydrateTemplate = (nextSettings: PlatformSettings) => {
    const source = nextSettings.default_school_template || {};
    setTemplateDraft({
      primaryColor: asString(source.primary_color, "#0f172a"),
      secondaryColor: asString(source.secondary_color, "#0ea5e9"),
      schoolMotto: asString(source.school_motto),
      academicYearStart: asString(source.academic_year_start),
      academicYearEnd: asString(source.academic_year_end),
      activeTerm: asString(source.active_term, "Term 1"),
      numberOfTerms: asNumberString(source.number_of_terms, "3"),
      schoolSections: Array.isArray(source.school_sections) && source.school_sections.length ? source.school_sections : ["Primary"],
      maxStudents: asNumberString(source.max_students, "300"),
      maxTeachers: asNumberString(source.max_teachers, "30"),
      maxAdmins: asNumberString(source.max_admins, "3")
    });
    setTemplateJson(prettyJson(source));
  };

  const hydrateSubscriptionDefaults = (nextSettings: PlatformSettings) => {
    const source = nextSettings.subscription_defaults || {};
    setSubscriptionDraft({
      defaultPlan: asString(source.default_plan, "basic"),
      renewalCycle: asString(source.renewal_cycle, "annual"),
      trialDays: asNumberString(source.trial_days, "14"),
      gracePeriodDays: asNumberString(source.grace_period_days, "7"),
      requireBillingApproval: asBoolean(source.require_billing_approval, false),
      blockedAccessMessage: asString(
        source.blocked_access_message,
        "This school subscription has expired. Please contact your administrator or billing support to renew access."
      )
    });
    setSubscriptionJson(prettyJson(source));
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [settingsRes, schoolsRes, flagsRes, financialRes, maintenanceRes] = await Promise.all([
        axios.get(getApiUrl("superadmin/settings/platform"), authHeaders),
        axios.get(getApiUrl("superadmin/schools"), authHeaders),
        axios.get(getApiUrl("superadmin/settings/feature-flags"), authHeaders),
        axios.get(getApiUrl("superadmin/financial-overview"), authHeaders),
        axios.get(getApiUrl("superadmin/maintenance/status"), authHeaders)
      ]);

      const nextSettings = settingsRes.data || {};
      const nextSchools = Array.isArray(schoolsRes.data) ? schoolsRes.data : [];
      const nextFlags: Record<string, FeatureFlagsRecord> = {};
      (flagsRes.data || []).forEach((entry: FeatureFlagsRecord) => {
        nextFlags[String(entry.tenant_id)] = entry;
      });

      setSettings(nextSettings);
      setSchools(nextSchools);
      setFeatureFlags(nextFlags);
      setFinancialOverview(financialRes.data || null);
      setMaintenanceStatus(maintenanceRes.data || {});
      hydrateTemplate(nextSettings);
      hydrateSubscriptionDefaults(nextSettings);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load system settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSchools = useMemo(() => {
    const term = schoolSearch.trim().toLowerCase();
    if (!term) return schools;
    return schools.filter((school) =>
      [school.name, school.subdomain, school.subscription_tier, school.subscription_status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [schools, schoolSearch]);

  const selectedSchoolRecord = useMemo(() => schools.find((school) => school.id === selectedSchool) || null, [schools, selectedSchool]);
  const selectedFlagsRecord = useMemo(() => (selectedSchool ? featureFlags[String(selectedSchool)] || null : null), [featureFlags, selectedSchool]);

  const schoolStats = useMemo(() => {
    const total = schools.length;
    const active = schools.filter((school) => String(school.status || "").toLowerCase() === "active").length;
    const inactive = schools.filter((school) => String(school.status || "").toLowerCase() === "inactive").length;
    const exempt = schools.filter((school) => Boolean(Number(school.is_subscription_exempt || 0))).length;
    return { total, active, inactive, exempt };
  }, [schools]);

  const subscriptionStats = {
    active: financialOverview?.subscriptions?.active || 0,
    expired: financialOverview?.subscriptions?.expired || 0,
    pastDue: financialOverview?.subscriptions?.past_due || 0
  };

  const savePlatformSetting = async (key: string, value: any, successText: string) => {
    try {
      setSavingSection(key);
      setError("");
      await axios.put(getApiUrl("superadmin/settings/platform"), { key, value }, authHeaders);
      const nextSettings = { ...settings, [key]: value };
      setSettings(nextSettings);
      if (key === "default_school_template") hydrateTemplate(nextSettings);
      if (key === "subscription_defaults") hydrateSubscriptionDefaults(nextSettings);
      setMessage(successText);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save settings.");
    } finally {
      setSavingSection("");
    }
  };

  const handleTemplateSave = async () => {
    const payload = {
      primary_color: templateDraft.primaryColor.trim(),
      secondary_color: templateDraft.secondaryColor.trim(),
      school_motto: templateDraft.schoolMotto.trim(),
      academic_year_start: templateDraft.academicYearStart || null,
      academic_year_end: templateDraft.academicYearEnd || null,
      active_term: templateDraft.activeTerm.trim(),
      number_of_terms: Math.max(1, Number(templateDraft.numberOfTerms) || 1),
      school_sections: Array.from(new Set(templateDraft.schoolSections)),
      max_students: Math.max(0, Number(templateDraft.maxStudents) || 0),
      max_teachers: Math.max(0, Number(templateDraft.maxTeachers) || 0),
      max_admins: Math.max(0, Number(templateDraft.maxAdmins) || 0)
    };
    await savePlatformSetting("default_school_template", payload, "Default school template updated.");
  };

  const handleTemplateJsonSave = async () => {
    const parsed = parseJson(templateJson, null);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      setError("Default school template must be a valid JSON object.");
      return;
    }
    await savePlatformSetting("default_school_template", parsed, "Default school template updated.");
  };

  const handleSubscriptionSave = async () => {
    const payload = {
      default_plan: subscriptionDraft.defaultPlan,
      renewal_cycle: subscriptionDraft.renewalCycle,
      trial_days: Math.max(0, Number(subscriptionDraft.trialDays) || 0),
      grace_period_days: Math.max(0, Number(subscriptionDraft.gracePeriodDays) || 0),
      require_billing_approval: subscriptionDraft.requireBillingApproval,
      blocked_access_message: subscriptionDraft.blockedAccessMessage.trim()
    };
    await savePlatformSetting("subscription_defaults", payload, "Subscription defaults updated.");
  };

  const handleSubscriptionJsonSave = async () => {
    const parsed = parseJson(subscriptionJson, null);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      setError("Subscription defaults must be a valid JSON object.");
      return;
    }
    await savePlatformSetting("subscription_defaults", parsed, "Subscription defaults updated.");
  };

  const selectSchool = (next: number | "") => {
    setSelectedSchool(next);
    const record = next ? featureFlags[String(next)] : null;
    setFeatureFlagsJson(prettyJson(record?.explicit_flags || {}));
  };

  const toggleSchoolModule = (moduleKey: string) => {
    if (!selectedSchool) return;
    const current = selectedFlagsRecord?.explicit_flags || {};
    const nextFlags = { ...current, [moduleKey]: !(selectedFlagsRecord?.flags?.[moduleKey] ?? false) };
    setFeatureFlagsJson(prettyJson(nextFlags));
  };

  const saveSchoolFlags = async () => {
    if (!selectedSchool) return;
    const parsed = parseJson(featureFlagsJson, null);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      setError("School feature overrides must be a valid JSON object.");
      return;
    }
    try {
      setSavingSection("feature_flags");
      setError("");
      const res = await axios.put(getApiUrl(`superadmin/settings/feature-flags/${selectedSchool}`), { flags: parsed }, authHeaders);
      const explicit = res.data?.flags || parsed;
      const planDefaults = selectedFlagsRecord?.plan_module_defaults || {};
      const resolved = { ...planDefaults, ...explicit };
      const nextRecord: FeatureFlagsRecord = {
        tenant_id: Number(selectedSchool),
        flags: resolved,
        explicit_flags: explicit,
        enabled_modules: MODULE_KEYS.filter((key) => resolved[key]),
        plan_package: selectedFlagsRecord?.plan_package || selectedSchoolRecord?.subscription_tier || "premium",
        plan_module_defaults: planDefaults
      };
      setFeatureFlags((prev) => ({ ...prev, [String(selectedSchool)]: nextRecord }));
      setFeatureFlagsJson(prettyJson(explicit));
      setMessage("School feature flags updated.");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save school feature flags.");
    } finally {
      setSavingSection("");
    }
  };

  const resetSchoolOverrides = () => {
    if (!selectedSchool) return;
    setFeatureFlagsJson("{}");
  };

  const cardClass = "bg-white rounded-3xl border border-slate-100 shadow-sm";

  if (loading) {
    return <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10 text-slate-500">Loading system settings...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1320px] mx-auto space-y-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Global System Settings</h1>
            <p className="text-slate-500 text-sm mt-2">Manage platform defaults, billing behavior, and school-level module overrides from one workspace.</p>
          </div>
          <button className="utopia-btn-secondary inline-flex items-center gap-2" onClick={loadData}>
            <RefreshCw size={16} /> Reload Settings
          </button>
        </div>

        {(message || error) && (
          <div className={`rounded-2xl border px-4 py-3 text-sm font-medium ${error ? "border-red-100 bg-red-50 text-red-700" : "border-emerald-100 bg-emerald-50 text-emerald-700"}`}>
            {error || message}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Schools</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{schoolStats.total}</p>
              </div>
              <Building2 className="text-sky-600" size={24} />
            </div>
            <p className="text-sm text-slate-500 mt-3">{schoolStats.active} active, {schoolStats.inactive} inactive</p>
          </div>
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Subscriptions</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{subscriptionStats.active}</p>
              </div>
              <ShieldCheck className="text-emerald-600" size={24} />
            </div>
            <p className="text-sm text-slate-500 mt-3">{subscriptionStats.expired} expired, {schoolStats.exempt} exempt</p>
          </div>
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Revenue</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{Number(financialOverview?.totals?.subscriptionRevenue || 0).toLocaleString()}</p>
              </div>
              <CircleDollarSign className="text-amber-600" size={24} />
            </div>
            <p className="text-sm text-slate-500 mt-3">Subscription revenue across all schools</p>
          </div>
          <div className={`${cardClass} p-5`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Maintenance</p>
                <p className="text-xl font-bold text-slate-900 mt-2">{maintenanceStatus?.enabled ? "Enabled" : "Normal"}</p>
              </div>
              <AlertTriangle className={maintenanceStatus?.enabled ? "text-amber-600" : "text-slate-400"} size={24} />
            </div>
            <p className="text-sm text-slate-500 mt-3">{maintenanceStatus?.message || "Platform-wide maintenance mode is currently off."}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.95fr] gap-8">
          <div className="space-y-8">
            <section className={`${cardClass} p-6`}>
              <div className="flex items-center gap-2 mb-5">
                <Settings2 size={18} className="text-slate-600" />
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Default School Template</h2>
                  <p className="text-sm text-slate-500">Set the baseline branding, academic structure, and quotas new schools inherit.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Primary Color</label>
                  <input className="utopia-input" value={templateDraft.primaryColor} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, primaryColor: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Secondary Color</label>
                  <input className="utopia-input" value={templateDraft.secondaryColor} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, secondaryColor: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Default Motto</label>
                  <input className="utopia-input" value={templateDraft.schoolMotto} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, schoolMotto: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Academic Year Start</label>
                  <input className="utopia-input" type="date" value={templateDraft.academicYearStart} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, academicYearStart: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Academic Year End</label>
                  <input className="utopia-input" type="date" value={templateDraft.academicYearEnd} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, academicYearEnd: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Active Term</label>
                  <input className="utopia-input" value={templateDraft.activeTerm} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, activeTerm: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Number of Terms</label>
                  <input className="utopia-input" type="number" min="1" value={templateDraft.numberOfTerms} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, numberOfTerms: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">School Sections</label>
                  <div className="flex flex-wrap gap-2">
                    {["Nursery", "Primary", "Secondary"].map((section) => {
                      const checked = templateDraft.schoolSections.includes(section);
                      return (
                        <button
                          type="button"
                          key={section}
                          className={`px-4 py-2 rounded-xl border text-sm font-semibold ${checked ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"}`}
                          onClick={() =>
                            setTemplateDraft((prev) => ({
                              ...prev,
                              schoolSections: checked ? prev.schoolSections.filter((item) => item !== section) : [...prev.schoolSections, section]
                            }))
                          }
                        >
                          {section}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Student Capacity</label>
                  <input className="utopia-input" type="number" min="0" value={templateDraft.maxStudents} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, maxStudents: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Teacher Capacity</label>
                  <input className="utopia-input" type="number" min="0" value={templateDraft.maxTeachers} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, maxTeachers: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Admin Slots</label>
                  <input className="utopia-input" type="number" min="0" value={templateDraft.maxAdmins} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, maxAdmins: e.target.value }))} />
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="utopia-btn-primary inline-flex items-center gap-2" onClick={handleTemplateSave} disabled={savingSection === "default_school_template"}>
                  <Save size={16} /> Save Template
                </button>
                <button type="button" className="utopia-btn-secondary" onClick={() => setTemplateJson(prettyJson(settings.default_school_template || {}))}>
                  Reset Advanced JSON
                </button>
              </div>
              <div className="mt-5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Advanced Template JSON</label>
                <textarea className="utopia-input min-h-[180px] font-mono text-xs" value={templateJson} onChange={(e) => setTemplateJson(e.target.value)} />
                <button className="utopia-btn-secondary mt-3" onClick={handleTemplateJsonSave}>Save Advanced Template JSON</button>
              </div>
            </section>

            <section className={`${cardClass} p-6`}>
              <div className="flex items-center gap-2 mb-5">
                <CircleDollarSign size={18} className="text-slate-600" />
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Subscription Defaults</h2>
                  <p className="text-sm text-slate-500">Set platform billing defaults and expired-access messaging for new and existing tenants.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Default Plan</label>
                  <select className="utopia-input" value={subscriptionDraft.defaultPlan} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, defaultPlan: e.target.value }))}>
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Renewal Cycle</label>
                  <select className="utopia-input" value={subscriptionDraft.renewalCycle} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, renewalCycle: e.target.value }))}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Trial Days</label>
                  <input className="utopia-input" type="number" min="0" value={subscriptionDraft.trialDays} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, trialDays: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Grace Period Days</label>
                  <input className="utopia-input" type="number" min="0" value={subscriptionDraft.gracePeriodDays} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, gracePeriodDays: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Expired Access Message</label>
                  <textarea className="utopia-input min-h-[120px]" value={subscriptionDraft.blockedAccessMessage} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, blockedAccessMessage: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-3 text-sm font-medium text-slate-700">
                    <input type="checkbox" checked={subscriptionDraft.requireBillingApproval} onChange={(e) => setSubscriptionDraft((prev) => ({ ...prev, requireBillingApproval: e.target.checked }))} />
                    Require billing approval before upgrade requests are finalized
                  </label>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="utopia-btn-primary inline-flex items-center gap-2" onClick={handleSubscriptionSave} disabled={savingSection === "subscription_defaults"}>
                  <Save size={16} /> Save Subscription Defaults
                </button>
                <button type="button" className="utopia-btn-secondary" onClick={() => setSubscriptionJson(prettyJson(settings.subscription_defaults || {}))}>
                  Reset Advanced JSON
                </button>
              </div>
              <div className="mt-5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Advanced Subscription JSON</label>
                <textarea className="utopia-input min-h-[180px] font-mono text-xs" value={subscriptionJson} onChange={(e) => setSubscriptionJson(e.target.value)} />
                <button className="utopia-btn-secondary mt-3" onClick={handleSubscriptionJsonSave}>Save Advanced Subscription JSON</button>
              </div>
            </section>
          </div>

          <div className="space-y-8">
            <section className={`${cardClass} p-6`}>
              <div className="flex items-center gap-2 mb-4">
                <Layers3 size={18} className="text-slate-600" />
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Per-School Module Overrides</h2>
                  <p className="text-sm text-slate-500">Select a school, review its package, and override enabled modules without editing raw JSON only.</p>
                </div>
              </div>
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className="utopia-input pl-10" placeholder="Search schools by name, subdomain, plan, or status" value={schoolSearch} onChange={(e) => setSchoolSearch(e.target.value)} />
              </div>
              <select className="utopia-input mb-4" value={selectedSchool} onChange={(e) => selectSchool(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Select school...</option>
                {filteredSchools.map((school) => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>

              {selectedSchoolRecord ? (
                <>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-4">
                    <p className="font-semibold text-slate-800">{selectedSchoolRecord.name}</p>
                    <p className="text-sm text-slate-500 mt-1">Plan: {selectedFlagsRecord?.plan_package || selectedSchoolRecord.subscription_tier || "premium"} | Status: {selectedSchoolRecord.subscription_status || "n/a"}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {MODULE_KEYS.map((moduleKey) => {
                      const enabled = Boolean(selectedFlagsRecord?.flags?.[moduleKey]);
                      const explicit = selectedFlagsRecord?.explicit_flags?.[moduleKey] !== undefined;
                      return (
                        <button
                          type="button"
                          key={moduleKey}
                          className={`rounded-2xl border p-3 text-left ${enabled ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
                          onClick={() => toggleSchoolModule(moduleKey)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-slate-800">{MODULE_LABELS[moduleKey]}</span>
                            <span className={`text-xs font-bold uppercase ${enabled ? "text-emerald-700" : "text-slate-400"}`}>{enabled ? "Enabled" : "Disabled"}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">{explicit ? "School override" : "Inherited from plan defaults"}</p>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button className="utopia-btn-primary inline-flex items-center gap-2" onClick={saveSchoolFlags} disabled={savingSection === "feature_flags"}>
                      <Save size={16} /> Save School Overrides
                    </button>
                    <button className="utopia-btn-secondary" onClick={resetSchoolOverrides}>Reset to Plan Defaults</button>
                  </div>
                  <div className="mt-5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Advanced School Override JSON</label>
                    <textarea className="utopia-input min-h-[180px] font-mono text-xs" value={featureFlagsJson} onChange={(e) => setFeatureFlagsJson(e.target.value)} />
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                  Select a school to manage its module overrides and compare them with its plan defaults.
                </div>
              )}
            </section>

            <section className={`${cardClass} p-6`}>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={18} className="text-slate-600" />
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Platform Policy Snapshot</h2>
                  <p className="text-sm text-slate-500">Quick visibility into default platform behavior and current operational state.</p>
                </div>
              </div>
              <div className="space-y-4 text-sm">
                <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-xs uppercase font-bold text-slate-400">Default Package</p>
                  <p className="font-semibold text-slate-800 mt-1">{subscriptionDraft.defaultPlan}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-xs uppercase font-bold text-slate-400">Expired Tenant Handling</p>
                  <p className="font-semibold text-slate-800 mt-1">{subscriptionDraft.gracePeriodDays} day grace period</p>
                  <p className="text-slate-500 mt-2">{subscriptionDraft.blockedAccessMessage}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-xs uppercase font-bold text-slate-400">Platform Health</p>
                  <p className="text-slate-700 mt-2">Past due subscriptions: {subscriptionStats.pastDue}</p>
                  <p className="text-slate-700 mt-1">Maintenance mode: {maintenanceStatus?.enabled ? "On" : "Off"}</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
